/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { useCallback } from "react";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { IResourceNode, ResourceList } from "../../../ui/components/ResourceList";
import { ResourceListDialog } from "./ResourceListDialog";
import { Typography } from "../../../ui/components/Typography";

export const ResourceListDialogComponent: TDialogComponent<ResourceListDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-module-core-resource-open-dialog");
	const params = dialog.getParams();

	const handleClose = useCallback(() => {
		dialog.close({
			resourceId: null
		});
	}, [ dialog ]);

	const handleResourceSelect = useCallback(
		(resource: IResourceNode) => {
			dialog.close({
				resourceId: resource.id
			});
		},
		[ dialog ]
	);

	return (
		<DialogPanel size={DIALOG_SIZE.MEDIUM} className={baseClass.toClassName()}>
			<DialogHeader title={params.title} icon={params.icon} onClose={handleClose} />
			<div className={baseClass.element("content").toClassName()}>
				{params.description && (
					<Typography Component="div" className={baseClass.element("description").toClassName()}>
						{params.description}
					</Typography>
				)}
				<ResourceList
					onSelect={handleResourceSelect}
					labelSearch="Select target directory"
					allowedResourceTypes={params.allowedResourceTypes}
					isDisabled={params.isDisabled}
					isSelectable={params.isSelectable}
				/>
			</div>
		</DialogPanel>
	);
};
